import React, { useState, useEffect } from "react";
import { formatDateTime } from "../utils/FormatDateTime";
import { BiChevronLeft, BiChevronRight } from "react-icons/bi";
import ShowModal from "../utils/ShowModal";

const GalleryComponent = ({ images = [], imageToDisplay, openingComponent, activeIndex = 0, className }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(activeIndex);

    const openModal = () => {
        // Only open the modal if there are images
        if (images.length > 0) {
            setCurrentIndex(activeIndex); // Set the initial index for the modal
            setIsModalOpen(true);
        }
    };

    const closeModal = () => setIsModalOpen(false);

    const handleNext = () => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    };

    const handlePrev = () => {
        setCurrentIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length);
    };

    const handleThumbnailClick = (index) => {
        setCurrentIndex(index);
    };

    const handleKeyDown = (e) => {
        if (e.key === "ArrowRight") {
            handleNext();
        } else if (e.key === "ArrowLeft") {
            handlePrev();
        } else if (e.key === "Escape") {
            closeModal();
        }
    };

    useEffect(() => {
        if (isModalOpen) {
            window.addEventListener("keydown", handleKeyDown);
        } else {
            window.removeEventListener("keydown", handleKeyDown);
        }

        // Cleanup the event listener on unmount
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [isModalOpen]);

    return (
        <div className="relative w-full">
            <div className="flex cursor-pointer" onClick={openModal}>
                {openingComponent || (imageToDisplay ? (
                    <div
                        className="relative transition-transform duration-300 ease-in-out transform hover:scale-105"
                    >
                        <img
                            src={imageToDisplay}
                            alt="Image"
                            className={className}
                        />
                    </div>
                ) : (
                    <></>
                ))
                }
            </div>
            <ShowModal contentLabel={"Gallery Modal"} modalWidth={"60%"} isOpen={isModalOpen} onRequestClose={closeModal}
                componentToPass={
                    <div className="w-full max-w-4xl mx-auto p-4 rounded-lg">
                        {/* Main Image Container */}
                        <div className="relative w-full h-96 mb-4 bg-gray-100 rounded-lg overflow-hidden">
                            {
                                images.length !== 0 ?
                                    <img
                                        src={images[currentIndex].image || images[currentIndex]}
                                        alt={`Image ${currentIndex + 1}`}
                                        className="w-full h-full object-contain"
                                    /> : <></>
                            }

                            {/* Navigation Buttons */}
                            {images.length > 1 && (
                                <>
                                    <button
                                        onClick={handlePrev}
                                        className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors"
                                        aria-label="Previous image"
                                    >
                                        <BiChevronLeft className="w-6 h-6 text-white" />
                                    </button>

                                    <button
                                        onClick={handleNext}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors"
                                        aria-label="Next image"
                                    >
                                        <BiChevronRight className="w-6 h-6 text-white" />
                                    </button>
                                </>
                            )}

                            {/* Date Overlay */}
                            {
                                images.length !== 0 ?
                                    <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white p-2 text-sm">
                                        {images[currentIndex].created_at
                                            ? `Uploaded on: ${formatDateTime(images[currentIndex].created_at || "N/A")}`
                                            : "No date available"
                                        }
                                    </div> : <></>
                            }
                        </div>

                        {/* Thumbnails Container */}
                        {
                            images.length !== 0 ?
                                <div className="relative">
                                    <div className="flex gap-2 overflow-x-auto py-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 justify-center">
                                        {images.map((img, idx) => (
                                            <button
                                                key={idx}
                                                onClick={() => handleThumbnailClick(idx)}
                                                className={`flex-none relative ${currentIndex === idx
                                                    ? 'ring-2 ring-primary scale-105 rounded'
                                                    : 'hover:ring-2 hover:ring-primary'
                                                    }`}
                                            >
                                                <img
                                                    src={img.image || img}
                                                    alt={`Thumbnail ${idx + 1}`}
                                                    className="w-16 h-16 object-cover rounded"
                                                />
                                            </button>
                                        ))}
                                    </div>
                                </div> : <></>
                        }
                    </div>
                } />
        </div>
    );
};

export default GalleryComponent;
